import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaPiggyBank, FaChartLine, FaInfoCircle, FaCheckCircle, FaQuestionCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const SavingsGoalCalculator = () => {
  const [formData, setFormData] = useState({
    goalAmount: '',
    currentSavings: '',
    monthlySavings: '',
    interestRate: '',
    yearsToGoal: ''
  });

  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize chart data
    const chartOptions = {
      series: [{
        name: 'Savings Progress',
        data: [0]
      }],
      options: {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          line: {
            width: 3
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth'
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '12px',
              colors: '#666'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px',
              colors: '#666'
            },
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            }
          }
        }
      }
    };
    setChartData(chartOptions);
  }, []);

  const calculateSavings = () => {
    const { goalAmount, currentSavings, monthlySavings, interestRate } = formData;

    if (!goalAmount || !currentSavings || !monthlySavings || !interestRate) {
      setError('Please fill in all required fields');
      return;
    }

    const goal = parseFloat(goalAmount);
    const savings = parseFloat(currentSavings);
    const monthly = parseFloat(monthlySavings);
    const rate = parseFloat(interestRate) / 100 / 12;

    if (isNaN(goal) || isNaN(savings) || isNaN(monthly) || isNaN(rate)) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    let months = 0;
    let totalSavings = savings;
    const savingsProgress = [];
    const monthsArray = [];

    while (totalSavings < goal) {
      totalSavings = totalSavings * (1 + rate) + monthly;
      months++;
      savingsProgress.push(totalSavings);
      monthsArray.push(months);

      if (months > 240) { // Prevent infinite loop
        break;
      }
    }

    const years = (months / 12).toFixed(1);
    const totalContributions = (months * monthly).toFixed(2);
    const totalInterest = (totalSavings - savings - (months * monthly)).toFixed(2);

    setResults({
      monthsToGoal: months,
      yearsToGoal: years,
      totalContributions: `$${totalContributions}`,
      totalInterest: `$${totalInterest}`,
      finalAmount: `$${totalSavings.toFixed(2)}`
    });

    // Update chart data
    if (chartData) {
      const updatedData = {
        ...chartData,
        series: [{
          name: 'Savings Progress',
          data: savingsProgress
        }],
        options: {
          ...chartData.options,
          xaxis: {
            ...chartData.options.xaxis,
            categories: monthsArray.map(m => `${Math.ceil(m/12)}y ${m%12}m`)
          }
        }
      };
      setChartData(updatedData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Savings Goal Calculator | Financial Tool</title>
        <meta name="description" content="Calculate how long it will take to reach your savings goal with our Savings Goal Calculator." />
        <meta name="keywords" content="savings calculator, financial tool, goal calculator, investment calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaPiggyBank />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Savings Goal Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Calculate Your Savings Progress</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            The Savings Goal Calculator helps you plan and track your progress towards reaching your financial goals.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="goalAmount">
                Goal Amount
              </label>
              <input
                type="number"
                id="goalAmount"
                name="goalAmount"
                value={formData.goalAmount}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="currentSavings">
                Current Savings
              </label>
              <input
                type="number"
                id="currentSavings"
                name="currentSavings"
                value={formData.currentSavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="monthlySavings">
                Monthly Savings
              </label>
              <input
                type="number"
                id="monthlySavings"
                name="monthlySavings"
                value={formData.monthlySavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="interestRate">
                Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <button
              onClick={calculateSavings}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate Savings Goal
            </button>

            {error && (
              <div className="text-red-500 text-center mt-4">
                {error}
              </div>
            )}
          </div>

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Time to Goal</h3>
                  <p className="text-xl font-medium">{results.yearsToGoal} years</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Contributions</h3>
                  <p className="text-xl font-medium">{results.totalContributions}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Interest Earned</h3>
                  <p className="text-xl font-medium">{results.totalInterest}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Final Amount</h3>
                  <p className="text-xl font-medium">{results.finalAmount}</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Savings Progress</h3>
                <div className="h-96">
                  {chartData && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="line"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Savings Goal Calculator <FaInfoCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ol className="list-decimal pl-8 space-y-4">
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your savings goal amount</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Input your current savings balance</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your monthly savings contribution</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Add your expected interest rate</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Click "Calculate Savings Goal" to see your results</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <FaChartLine className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Time to goal calculation</li>
                  <li>Compound interest calculation</li>
                  <li>Monthly savings tracking</li>
                  <li>Visual progress chart</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Plan your savings strategy</li>
                  <li>Track progress towards goals</li>
                  <li>See the power of compound interest</li>
                  <li>Adjust savings to meet goals faster</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <FaQuestionCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the calculator work?</h3>
                <p className="text-gray-700">
                  The calculator uses compound interest to project your savings growth based on your monthly contributions
                  and the interest rate you expect to earn.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What factors affect my savings growth?</h3>
                <p className="text-gray-700">
                  Your monthly contribution amount, interest rate, and current savings balance are the key factors that
                  determine how quickly you'll reach your goal.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How can I reach my goal faster?</h3>
                <p className="text-gray-700">
                  You can reach your goal faster by increasing your monthly contributions, finding a higher interest rate,
                  or adjusting your goal amount.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Is this calculator accurate?</h3>
                <p className="text-gray-700">
                  This calculator provides an estimate based on the inputs you provide. Actual results may vary based on
                  market conditions and other factors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsGoalCalculator;
